/**
 * Application entry point that bootstraps the LOA Frontend.
 *
 * @module main
 * @packageDocumentation
 *
 * @remarks
 * This is the main entry point for the LOA Frontend application.
 * It initializes the Application instance, sets up React rendering,
 * and mounts the app to the DOM.
 *
 * ## Bootstrap Process
 *
 * 1. Import North Star design system styles
 * 2. Import application-specific styles
 * 3. Conditionally start the MSW Service Worker (when `VITE_MSW_ENABLED=true`)
 * 4. Create Application instance with environment configuration
 * 5. Render the React app with StrictMode, QueryClientProvider, and AppProvider
 * 6. Display version info in non-production environments
 *
 * ## Error Handling
 *
 * If the Application fails to initialize, the error is caught and
 * rendered directly to the DOM for debugging purposes.
 */

import { AlertProvider } from "@mitratech/north-star";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oauth2-code-pkce";

// Application styles
import "./style/main.scss";

import { AppProvider } from "./app/Application.provider.tsx";
import { Application } from "./app/Application.ts";
import { App } from "./app/Application.tsx";

import { getConfig } from "./app/utils.ts";
import { OAuthBridge } from "./components/auth";
import { Branch } from "./components/branch/Branch.tsx";
import { oauthConfig } from "./config/oauth.config";
import { installChunkReloadRecovery } from "./lib/chunkReloadRecovery.ts";
import { queryClient } from "./lib/queryClient.ts";
import { enableMocking } from "./mocks/enableMocking.ts";

/**
 * Bootstrap the application.
 *
 * MSW is started (if enabled) before React renders so that any requests
 * fired during the initial render are intercepted correctly.
 */
/** Renders a plain-text failure message directly to the DOM — used when the app can't even mount React normally (e.g. `enableMocking()` rejected). */
function renderBootstrapFailure(context: string, err: unknown): void {
  // eslint-disable-next-line no-console
  console.error(`[main] ${context}`, err);

  const detail = err instanceof Error ? (err.stack ?? err.message) : String(err);
  const message = import.meta.env.DEV
    ? `${context}\n\n${detail}`
    : "Something went wrong while starting the app. Please try again later.";

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <pre style={{ padding: 16, color: "#b91c1c", whiteSpace: "pre-wrap" }}>{message}</pre>
    </StrictMode>,
  );
}

// Recover from stale route chunks after a deploy: if a dynamic import 404s
// (old hash no longer on the server), reload once to fetch the current build.
installChunkReloadRecovery();

enableMocking()
  .then(() => {
    try {
      const app = new Application(getConfig());

      createRoot(document.getElementById("root")!).render(
        <StrictMode>
          <QueryClientProvider client={queryClient}>
            <AuthProvider authConfig={oauthConfig}>
              <AppProvider app={app}>
                <AlertProvider>
                  <OAuthBridge>
                    <App />
                    <Branch branch={app.version.branch!} release={app.version.release!} />
                  </OAuthBridge>
                </AlertProvider>
              </AppProvider>
            </AuthProvider>
            {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition={"bottom-left"} />}
          </QueryClientProvider>
        </StrictMode>,
      );
    } catch (err) {
      renderBootstrapFailure("Failed to render the application.", err);
    }
  })
  .catch((err: unknown) => {
    // enableMocking() itself rejected (e.g. the MSW Service Worker failed to
    // register/activate) — without this catch, the app never renders and the
    // page stays silently blank forever, with no error visible anywhere.
    renderBootstrapFailure(
      "Failed to start the app: enableMocking() rejected (MSW Service Worker setup likely failed).",
      err,
    );
  });
